import axios, { AxiosInstance } from 'axios';
import { isNil } from 'lodash';
import {
  API_STAKING_BASE_URL,
  API_STAKING_TESTNET_BASE_URL,
} from 'utils/constants/common';
import { NetworkChainIds } from '@dapp/multi-lib/network';

const getStakingBaseURL = () => {
  if (typeof window === 'undefined') return API_STAKING_BASE_URL;

  return window.location.pathname.includes('/testnet/staking')
    ? API_STAKING_TESTNET_BASE_URL
    : API_STAKING_BASE_URL;
};

export type StakingPool = {
  id: number;
  chainId: NetworkChainIds;
  stakingContractAddress: `0x${string}`;
  rewardsContractAddress: `0x${string}`;
  depositTokenAddress: `0x${string}`;
  depositTokenSymbol: string;
  depositTokenName: string;
  depositTokenDecimals: 18;
  rewardsTokenAddress: `0x${string}`;
  rewardsTokenSymbol: string;
  rewardsTokenName: string;
  rewardsTokenDecimals: 18;
  tokensStaked: string;
  iconUrl: string;
  totalValueLockedUSD: string;
  totalRewards: string;
  totalSREP: string;
  rewardsEmissionPeriod: string;
  rewardsCommissionPercentage: string;
  totalReputationScore: string;
  MAX_LOCKING_PERIOD: string;
  earlyUnlockFeePerDayPercentage: string;
  earlyUnlockFeesPercentage: string;
  projectToken: string;
  averageAPR: string;
  defaultLockingPeriods: number[];
  name: string;
  icon: string;
  token: string;
};

export type UserPoolInfo = {
  walletAddress: `0x${string}`;
  contractAddress: `0x${string}`;
  totalStaked: string;
  lockDate: string;
  unlockDate: string;
  reputationScore: string;
  claimable: string;
  apr: string;
};

export type Calculation = {
  apr: string;
  tentativeREP: string;
  existingREPAcrossPools: string;
  totalStaked: string;
  unlockDate: string;
};

export type DepositCalculation = Calculation & {
  claimable: string;
  lockDate: string;
  contractAddress: `0x${string}`;
  walletAddress: `0x${string}`;
};

export type ExtentCalculation = Calculation;

export type WithdrawCalculation = Calculation & {
  daysLeft: string;
  netReceivable: string;
  penalty: string;
  withdrawFeePercentage: string;
};

export type StakingProject = {
  id: number;
  identifier: string;
  tokenSymbol: string;
  tokenName: string;
  website: string;
  title: string;
  description: string;
  iconUrl: string;
  imageUrl: string;
  reputationDeck: string;
  telegram: string;
  discord: string;
  blog: string;
  twitter: string;
  whitepaper: string;
  createdAt: string;
  updatedAt: string;
};

export type WalletRewardsInfo = {
  srep: string;
};

export class StakingAPI {
  private axiosInstance: AxiosInstance;

  constructor() {
    this.axiosInstance = axios.create();
    this.axiosInstance.interceptors.request.use((config) => {
      config.baseURL = getStakingBaseURL();
      config.timeout = 120000;
      return config;
    });
  }
  async loadPoolList(token?: string): Promise<StakingPool[]> {
    try {
      const response = await this.axiosInstance.get(`/staking/v1/pools`, {
        params: { projectToken: token },
      });
      const filteredPools = response?.data?.filter(
        (pool: StakingPool) => pool?.projectToken !== 'GNESS'
      );

      return filteredPools;
    } catch (error) {
      console.error('Error loading staking pool list', error);
      return [];
    }
  }

  async loadExtendCalcInfo({
    poolContractAddress,
    walletAddress,
    chainId,
    lockedPeriod,
  }: {
    poolContractAddress: `0x${string}`;
    walletAddress: `0x${string}`;
    chainId: NetworkChainIds;
    lockedPeriod: number;
  }): Promise<DepositCalculation> {
    try {
      const response = await this.axiosInstance.post(
        `/staking/v1/pools/extend/${chainId}/${poolContractAddress}/${walletAddress}`,
        {
          seconds_locked: lockedPeriod,
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error loading extend calculation info', error);
      throw error;
    }
  }

  async loadStakingProjectDetails({
    tokenSymbol,
  }: {
    tokenSymbol: string;
  }): Promise<StakingProject> {
    try {
      const response = await this.axiosInstance.get(
        `/staking/v1/project/${tokenSymbol}`
      );
      return response.data;
    } catch (error) {
      console.error('Error loading staking project details', error);
      throw error;
    }
  }

  async loadWithdrawCalcInfo({
    poolContractAddress,
    walletAddress,
    chainId,
    tokenAmount,
  }: {
    poolContractAddress: `0x${string}`;
    walletAddress: `0x${string}`;
    chainId: NetworkChainIds;
    tokenAmount: string;
  }): Promise<WithdrawCalculation> {
    try {
      const response = await this.axiosInstance.post(
        `/staking/v1/pools/withdraw/${chainId}/${poolContractAddress}/${walletAddress}`,
        {
          withdraw_sdao_amount: tokenAmount,
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error loading withdraw calculation info', error);
      throw error;
    }
  }

  async updatePoolMetadata(): Promise<void> {
    try {
      const response = await this.axiosInstance.post(
        `/staking/v1/pools/update-pool-metadata`
      );
      return response.data;
    } catch (error) {
      console.error('Error updating pool metadata', error);
      throw error;
    }
  }

  async loadWalletSREP({
    walletAddress,
    depositTokenSymbol,
  }: {
    walletAddress: `0x${string}`;
    depositTokenSymbol?: string | null;
  }): Promise<WalletRewardsInfo> {
    try {
      let endpoint = `/staking/v1/pools/rewards/${walletAddress}`;

      if (!isNil(depositTokenSymbol)) {
        endpoint = `/staking/v1/pools/rewards/${walletAddress}/${depositTokenSymbol}`;
      }

      const response = await this.axiosInstance.get(endpoint);
      return response.data;
    } catch (error) {
      console.error('Error loading wallet SREP info', error);
      throw error;
    }
  }
}

export const stakingAPI = new StakingAPI();
